import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import { AuthenticatedMedia, openAuthenticatedMedia } from "./AuthenticatedMedia";
import { deleteMember, fetchMembers, MemberRow, updateMember } from "./api";
import ActionToast, { ToastAction } from "./ActionToast";
import "./members.css";

const GENDER_OPTIONS = ["Male", "Female", "Other"];
const NATIONALITY_OPTIONS = ["India", "Other"];

const newestMembersFirst = (members: MemberRow[]) => [...members].sort((left, right) => {
  const leftTime = Date.parse(left.createdAt || "");
  const rightTime = Date.parse(right.createdAt || "");
  const normalizedLeftTime = Number.isFinite(leftTime) ? leftTime : Number.NEGATIVE_INFINITY;
  const normalizedRightTime = Number.isFinite(rightTime) ? rightTime : Number.NEGATIVE_INFINITY;
  if (normalizedLeftTime !== normalizedRightTime) return normalizedRightTime - normalizedLeftTime;
  return Number(right.id || 0) - Number(left.id || 0);
});
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const MemberProfileAvatar: React.FC<{ src?: string; name: string; initials: string }> = ({ src, name, initials }) => {
  const [failed, setFailed] = useState(false);
  return src && !failed ? (
    <img className="member-profile-avatar" src={src} alt={name || "Member"} onError={() => setFailed(true)} />
  ) : (
    <span className="member-profile-avatar member-profile-initials" aria-label="No member photo">{initials || "-"}</span>
  );
};

const MemberList: React.FC = () => {
  const [rows, setRows] = useState<MemberRow[]>([]);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [viewRow, setViewRow] = useState<MemberRow | null>(null);
  const [editRow, setEditRow] = useState<MemberRow | null>(null);
  const [showStateOptions, setShowStateOptions] = useState(false);
  const [stateSearchValue, setStateSearchValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const [resumeDragOver, setResumeDragOver] = useState(false);
  const [toastAction, setToastAction] = useState<ToastAction | null>(null);
  const activeDialog = viewRow ? "view" : editRow ? "edit" : null;

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setRows(newestMembersFirst(await fetchMembers())); }
    catch (err: any) { setError(err.message || "Failed to fetch members"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void loadMembers(); }, [loadMembers]);

  useEffect(() => {
    if (!activeDialog) return;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { setViewRow(null); setEditRow(null); }
      if (event.key === "Tab" && dialogRef.current) {
        const controls = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'));
        if (!controls.length) { event.preventDefault(); return; }
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => { document.removeEventListener("keydown", onKeyDown); };
  }, [activeDialog]);
  useEffect(() => { if (!activeDialog) returnFocusRef.current?.focus(); }, [activeDialog]);

  useEffect(() => {
    if (!toastAction) return;
    const timer = setTimeout(() => setToastAction(null), 6500);
    return () => clearTimeout(timer);
  }, [toastAction]);

  const states = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => (r.state || "").trim()).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const searchMatch =
        !q ||
        r.fullName?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.memberId?.toLowerCase().includes(q);
      const stateMatch = stateFilter === "all" || (r.state || "").trim().toLowerCase() === stateFilter.toLowerCase();
      const genderMatch = genderFilter === "all" || (r.gender || "").trim().toLowerCase() === genderFilter.toLowerCase();
      return searchMatch && stateMatch && genderMatch;
    });
  }, [rows, search, stateFilter, genderFilter]);

  const getInitials = (name: string) =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("");

  const onDelete = async (id: number) => {
    if (!window.confirm("Permanently delete this member's database record? This cannot be undone.")) return;
    try {
      await deleteMember(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
      setToastAction("deleted");
    } catch (err: any) {
      alert(err.message || "Delete failed");
    }
  };

  const onSave = async () => {
    if (!editRow) return;
    const hasValidState = stateOptions.some(
      (option) => option.toLowerCase() === (editRow.state || "").trim().toLowerCase()
    );
    if (!hasValidState) {
      alert("Please select a valid state from the dropdown.");
      return;
    }
    setSaving(true);
    try {
      const result = await updateMember(editRow);
      if ((editRow.photoFile && result.photoUrl === undefined) || (editRow.resumeFile && result.resumeUrl === undefined)) {
        await loadMembers();
      } else {
        const updatedRow = { ...editRow };
        delete updatedRow.photoFile;
        delete updatedRow.resumeFile;
        setRows((prev) => prev.map((item) => (item.id === editRow.id ? {
          ...item, ...updatedRow,
          photoUrl: result.photoUrl === undefined ? item.photoUrl : result.photoUrl || undefined,
          resumeUrl: result.resumeUrl === undefined ? item.resumeUrl : result.resumeUrl || undefined,
        } : item)));
      }
      setEditRow(null);
      setToastAction("saved");
    } catch (err: any) {
      alert(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const formatDisplayDate = (value?: string) => {
    if (!value) return "-";
    const raw = String(value).trim();

    // Keep explicit day-month-year dates untouched.
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      return raw;
    }

    // Plain date values (YYYY-MM-DD): map directly, no timezone conversion.
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.slice(0, 10).split("-");
      return `${day}-${month}-${year}`;
    }

    // Timestamp values: render in IST to avoid one-day drift from UTC conversion.
    if (raw.includes("T")) {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw;
      return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
    }

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear());
    return `${day}-${month}-${year}`;
  };

  const toInputDate = (value?: string) => {
    if (!value) return "";
    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split("-");
      return `${year}-${month}-${day}`;
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const normalizedGenderValue = (value?: string) => {
    const raw = (value || "").trim();
    if (!raw) return "";
    const found = GENDER_OPTIONS.find((option) => option.toLowerCase() === raw.toLowerCase());
    return found || raw;
  };

  const normalizedNationalityValue = (value?: string) => {
    const raw = (value || "").trim();
    if (!raw) return "";
    const found = NATIONALITY_OPTIONS.find((option) => option.toLowerCase() === raw.toLowerCase());
    return found || raw;
  };

  const stateOptions = useMemo(() => {
    const merged = [...INDIAN_STATES, ...states, (editRow?.state || "").trim()].filter(Boolean);
    return Array.from(new Set(merged)).sort((a, b) => a.localeCompare(b));
  }, [states, editRow?.state]);

  const filteredStateOptions = useMemo(() => {
    const q = stateSearchValue.trim().toLowerCase();
    if (!q) return stateOptions;
    return stateOptions.filter((option) => option.toLowerCase().includes(q));
  }, [stateOptions, stateSearchValue]);

  return (
    <DashboardLayout>
      <div className="members-page">
      <ActionToast action={toastAction} />
      <h1 className="list-title">Members</h1>
      <p className="list-subtitle">{loading ? "Loading members…" : error ? "Members unavailable" : `${rows.length} total members`}</p>
      {error && <div className="members-error" role="alert">{error} <button type="button" onClick={loadMembers}>Retry</button></div>}
      {!loading && !error && <>
      <div className="list-toolbar list-toolbar-members">
        <div className="search-wrap">
          <label htmlFor="members-search">Search</label>
          <i className="fa-solid fa-magnifying-glass search-icon" />
          <input
            className="form-control search-input"
            id="members-search"
            placeholder="Search by name, email, ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label>State<select className="form-select toolbar-select" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
          <option value="all">All States</option>
          {states.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select></label>
        <label>Gender<select className="form-select toolbar-select" value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select></label>
      </div>

      <div className="table-shell table-responsive">
        <table className="table data-table mb-0">
          <thead>
            <tr>
              <th style={{ width: 70 }} />
              <th>ID</th>
              <th>Name</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>City/State</th>
              <th>Joined</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id}>
                <td>
                  <span className="table-avatar">{getInitials(row.fullName || "")}</span>
                </td>
                <td>{row.memberId || row.id}</td>
                <td>{row.fullName}</td>
                <td>{row.mobile}</td>
                <td>{row.email}</td>
                <td>
                  {row.city}, {row.state}
                </td>
                <td>{row.createdAt || "-"}</td>
                <td className="text-end">
                  <div className="action-icons">
                    <button type="button" className="icon-btn" title="View Details" aria-label={`View ${row.fullName}`} onClick={(event) => { returnFocusRef.current = event.currentTarget; setViewRow(row); }}>
                      <i className="fa-regular fa-eye" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      title="Edit"
                      aria-label={`Edit ${row.fullName}`}
                      onClick={(event) => {
                        returnFocusRef.current = event.currentTarget;
                        setEditRow({ ...row });
                        setStateSearchValue(row.state || "");
                        setShowStateOptions(false);
                      }}
                    >
                      <i className="fa-regular fa-pen-to-square" />
                    </button>
                    <button type="button" className="icon-btn icon-btn-danger" title="Delete" aria-label={`Delete ${row.fullName}`} onClick={() => onDelete(row.id)}>
                      <i className="fa-regular fa-trash-can" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-4 text-muted">
                  No members found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </>}

      {viewRow && (
        <div className="dash-modal-overlay" onClick={() => setViewRow(null)}>
          <div className="dash-modal" role="dialog" aria-modal="true" aria-labelledby="member-view-title" tabIndex={-1} ref={dialogRef} onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 id="member-view-title" className="dash-modal-title">Member Details</h3>
              <button type="button" className="dash-modal-close" aria-label="Close member details" onClick={() => setViewRow(null)}>
                x
              </button>
            </div>
            <div className="dash-modal-body member-profile">
              <div className="member-profile-header">
                {viewRow.photoUrl ? <AuthenticatedMedia endpoint={`/files/members/${viewRow.id}/photo`} alt={viewRow.fullName || "Member"} className="member-profile-avatar" fallback={<MemberProfileAvatar name={viewRow.fullName || ""} initials={getInitials(viewRow.fullName || "")} />} /> : <MemberProfileAvatar name={viewRow.fullName || ""} initials={getInitials(viewRow.fullName || "")} />}
                <div className="member-profile-identity">
                  <h4>{viewRow.fullName || "-"}</h4>
                  <p>Member ID: {viewRow.memberId || "-"}</p>
                  <p>Joined: {formatDisplayDate(viewRow.createdAt)}</p>
                </div>
              </div>
              <section className="member-profile-section" aria-labelledby="member-profile-contact">
                <h4 id="member-profile-contact">Contact</h4>
                <dl><div><dt>Mobile</dt><dd>{viewRow.mobile || "-"}</dd></div><div><dt>Email</dt><dd>{viewRow.email || "-"}</dd></div></dl>
              </section>
              <section className="member-profile-section" aria-labelledby="member-profile-personal">
                <h4 id="member-profile-personal">Personal Information</h4>
                <dl>
                  <div><dt>Aadhar</dt><dd>{viewRow.aadhar || "-"}</dd></div>
                  <div><dt>Reference By</dt><dd>{viewRow.referenceBy || "-"}</dd></div>
                  <div><dt>Gender</dt><dd>{viewRow.gender || "-"}</dd></div>
                  <div><dt>Date of Birth</dt><dd>{formatDisplayDate(viewRow.dateOfBirth)}</dd></div>
                  <div><dt>Father's Name</dt><dd>{viewRow.fathersName || "-"}</dd></div>
                  <div><dt>Nationality</dt><dd>{viewRow.nationality || "-"}</dd></div>
                  <div><dt>Qualification</dt><dd>{viewRow.qualification || "-"}</dd></div>
                  <div><dt>Area of Operation</dt><dd>{viewRow.areaOfOperation || "-"}</dd></div>
                  <div><dt>Experience</dt><dd>{viewRow.experience || "-"}</dd></div>
                  <div className="member-profile-wide"><dt>Brief About Yourself</dt><dd>{viewRow.briefAboutYourself || "-"}</dd></div>
                </dl>
              </section>
              <section className="member-profile-section" aria-labelledby="member-profile-address">
                <h4 id="member-profile-address">Address</h4>
                <dl><div><dt>State</dt><dd>{viewRow.state || "-"}</dd></div><div><dt>City</dt><dd>{viewRow.city || "-"}</dd></div><div><dt>Pin Code</dt><dd>{viewRow.pinCode || "-"}</dd></div><div className="member-profile-wide"><dt>Address</dt><dd>{viewRow.address || "-"}</dd></div></dl>
              </section>
              <section className="member-profile-section" aria-labelledby="member-profile-documents">
                <h4 id="member-profile-documents">Documents</h4>
                <dl><div><dt>Resume</dt><dd>{viewRow.resumeUrl ? <button type="button" onClick={() => void openAuthenticatedMedia(`/files/members/${viewRow.id}/resume`)}>View Resume</button> : "-"}</dd></div></dl>
              </section>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setViewRow(null)}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setEditRow({ ...viewRow });
                  setStateSearchValue(viewRow.state || "");
                  setShowStateOptions(false);
                  setViewRow(null);
                }}
              >
                Edit Member
              </button>
            </div>
          </div>
        </div>
      )}

      {editRow && (
        <div className="dash-modal-overlay" onClick={() => setEditRow(null)}>
          <div className="dash-modal" role="dialog" aria-modal="true" aria-labelledby="member-edit-title" tabIndex={-1} ref={dialogRef} onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 id="member-edit-title" className="dash-modal-title">Edit Member</h3>
              <button type="button" className="dash-modal-close" aria-label="Close edit member" onClick={() => setEditRow(null)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div className="edit-grid">
                <div className="edit-field">
                  <label className="edit-label">Full Name</label>
                  <input aria-label="Full Name" className="form-control" placeholder="Enter full name" value={editRow.fullName || ""} onChange={(e) => setEditRow({ ...editRow, fullName: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Mobile</label>
                  <input aria-label="Mobile" className="form-control" placeholder="Enter mobile number" value={editRow.mobile || ""} onChange={(e) => setEditRow({ ...editRow, mobile: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Email</label>
                  <input aria-label="Email" className="form-control" placeholder="Enter email address" value={editRow.email || ""} onChange={(e) => setEditRow({ ...editRow, email: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Aadhar</label>
                  <input aria-label="Aadhar" className="form-control" placeholder="Enter Aadhar number" value={editRow.aadhar || ""} onChange={(e) => setEditRow({ ...editRow, aadhar: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Reference By</label>
                  <input aria-label="Reference By" className="form-control" placeholder="Enter reference by" value={editRow.referenceBy || ""} onChange={(e) => setEditRow({ ...editRow, referenceBy: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Gender</label>
                  <select aria-label="Gender" className="form-select" value={normalizedGenderValue(editRow.gender)} onChange={(e) => setEditRow({ ...editRow, gender: e.target.value })}>
                    <option value="">Select Gender</option>
                    {GENDER_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    {normalizedGenderValue(editRow.gender) && !GENDER_OPTIONS.includes(normalizedGenderValue(editRow.gender)) && (
                      <option value={normalizedGenderValue(editRow.gender)}>{normalizedGenderValue(editRow.gender)}</option>
                    )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label">Date of Birth</label>
                  <input aria-label="Date of Birth" className="form-control" type="date" placeholder="Select date of birth" title="Select date of birth" value={toInputDate(editRow.dateOfBirth)} onChange={(e) => setEditRow({ ...editRow, dateOfBirth: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Father's Name</label>
                  <input aria-label="Father's Name" className="form-control" placeholder="Enter father's name" value={editRow.fathersName || ""} onChange={(e) => setEditRow({ ...editRow, fathersName: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">State</label>
                  <div style={{ position: "relative" }}>
                    <input
                      className="form-control"
                      aria-label="State"
                      placeholder="Search or select state"
                      value={stateSearchValue}
                      onFocus={() => setShowStateOptions(true)}
                      onChange={(e) => {
                        setStateSearchValue(e.target.value);
                        setShowStateOptions(true);
                      }}
                      onBlur={() => {
                        const matched = stateOptions.find(
                          (option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase()
                        );
                        if (matched) {
                          setStateSearchValue(matched);
                          setEditRow({ ...editRow, state: matched });
                        } else {
                          setStateSearchValue(editRow.state || "");
                        }
                        setTimeout(() => setShowStateOptions(false), 120);
                      }}
                    />
                    {showStateOptions && filteredStateOptions.length > 0 && (
                      <div
                        className="list-group"
                        style={{
                          position: "absolute",
                          zIndex: 20,
                          width: "100%",
                          maxHeight: "180px",
                          overflowY: "auto",
                          border: "1px solid #dfe5ef",
                          borderRadius: "8px",
                          marginTop: "6px",
                          background: "#fff",
                        }}
                      >
                        {filteredStateOptions.slice(0, 50).map((option) => (
                          <button
                            key={option}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setStateSearchValue(option);
                              setEditRow({ ...editRow, state: option });
                              setShowStateOptions(false);
                            }}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="edit-field">
                  <label className="edit-label">City</label>
                  <input aria-label="City" className="form-control" placeholder="Enter city" value={editRow.city || ""} onChange={(e) => setEditRow({ ...editRow, city: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Pin Code</label>
                  <input aria-label="Pin Code" className="form-control" placeholder="Enter pin code" value={editRow.pinCode || ""} onChange={(e) => setEditRow({ ...editRow, pinCode: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Address</label>
                  <input aria-label="Address" className="form-control" placeholder="Enter full address" value={editRow.address || ""} onChange={(e) => setEditRow({ ...editRow, address: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Nationality</label>
                  <select aria-label="Nationality" className="form-select" value={normalizedNationalityValue(editRow.nationality)} onChange={(e) => setEditRow({ ...editRow, nationality: e.target.value })}>
                    {NATIONALITY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    {normalizedNationalityValue(editRow.nationality) &&
                      !NATIONALITY_OPTIONS.includes(normalizedNationalityValue(editRow.nationality)) && (
                        <option value={normalizedNationalityValue(editRow.nationality)}>{normalizedNationalityValue(editRow.nationality)}</option>
                      )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label">Qualification</label>
                  <input aria-label="Qualification" className="form-control" placeholder="Enter qualification" value={editRow.qualification || ""} onChange={(e) => setEditRow({ ...editRow, qualification: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Area of Operation</label>
                  <input aria-label="Area of Operation" className="form-control" placeholder="Enter area of operation" value={editRow.areaOfOperation || ""} onChange={(e) => setEditRow({ ...editRow, areaOfOperation: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Experience</label>
                  <input aria-label="Experience" className="form-control" placeholder="Enter experience" value={editRow.experience || ""} onChange={(e) => setEditRow({ ...editRow, experience: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Brief About Yourself</label>
                  <textarea aria-label="Brief About Yourself" className="form-control" rows={3} placeholder="Write brief about yourself" value={editRow.briefAboutYourself || ""} onChange={(e) => setEditRow({ ...editRow, briefAboutYourself: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Resume Upload</label>
                  {editRow.resumeUrl && !editRow.resumeFile && (
                    <div className="text-muted mb-2">Existing resume saved <button type="button" className="btn btn-link p-0" onClick={() => void openAuthenticatedMedia(`/files/members/${editRow.id}/resume`)}>View existing resume</button></div>
                  )}
                  {editRow.resumeFile && <div className="text-muted mb-2">Replacement selected: <strong>{editRow.resumeFile.name}</strong></div>}
                  <label
                    className={`dash-upload-box ${resumeDragOver ? "is-dragover" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setResumeDragOver(true);
                    }}
                    onDragLeave={() => setResumeDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setResumeDragOver(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) setEditRow({ ...editRow, resumeFile: file });
                    }}
                  >
                    <i className="fa-regular fa-file-lines" />
                    <input
                      type="file"
                      aria-label="Resume Upload (PDF, DOC or DOCX)"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="dash-upload-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setEditRow({ ...editRow, resumeFile: file });
                      }}
                    />
                  </label>
                  <small>PDF, DOC or DOCX.</small>
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Photo Upload</label>
                  {editRow.photoUrl && !editRow.photoFile && <div className="mb-2"><AuthenticatedMedia endpoint={`/files/members/${editRow.id}/photo`} alt="Existing member photo" className="member-profile-avatar" fallback={<span className="text-muted">Existing photo unavailable</span>} /></div>}
                  {editRow.photoFile && <div className="text-muted mb-2">Replacement selected: <strong>{editRow.photoFile.name}</strong></div>}
                  <label
                    className={`dash-upload-box ${photoDragOver ? "is-dragover" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setPhotoDragOver(true);
                    }}
                    onDragLeave={() => setPhotoDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setPhotoDragOver(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) setEditRow({ ...editRow, photoFile: file });
                    }}
                  >
                    <i className="fa-regular fa-image" />
                    <input
                      type="file"
                      aria-label="Photo Upload (JPEG, PNG or WebP)"
                      accept="image/jpeg,image/png,image/webp"
                      className="dash-upload-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setEditRow({ ...editRow, photoFile: file });
                      }}
                    />
                  </label>
                  <small>JPEG, PNG or WebP.</small>
                </div>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEditRow(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={saving} onClick={onSave}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
};

export default MemberList;
